import { LucideIcon, Mail } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { imageUrl } from '../../../../helpers'

export type IntegrationKey =
  | 'wow'
  | 'sheets'
  | 'warcraftLogs'
  | 'discord'
  | 'raidbots'
  | 'rcLootCouncil'
  | 'questionablyEpic'
  | 'email'

export type IntegrationRef =
  | IntegrationKey
  | { key: IntegrationKey; label: string }

export const integrationKeyOf = (ref: IntegrationRef): IntegrationKey =>
  typeof ref === 'string' ? ref : ref.key

interface Integration {
  name: string
  href?: string
  src?: string
  icon?: LucideIcon
}

export const integrations: Record<IntegrationKey, Integration> = {
  wow: {
    name: "Automated roster sync with Blizzard's API",
    href: 'https://worldofwarcraft.blizzard.com',
    src: imageUrl('wow-logo.svg'),
  },
  sheets: {
    name: "Extensive spreadsheet to track your team's progress",
    href: '/demo',
    src: imageUrl('sheets-logo.png'),
  },
  warcraftLogs: {
    name: 'Warcraft Logs',
    href: 'https://www.warcraftlogs.com',
    src: imageUrl('wcl-logo.png'),
  },
  discord: {
    name: 'Discord',
    href: 'https://discord.gg/86eUAFz',
    src: imageUrl('discord-logo-blue.svg'),
  },
  raidbots: {
    name: 'Upload Raidbots Droptimizer reports',
    href: 'https://www.raidbots.com',
    src: imageUrl('raidbots-logo-head.svg'),
  },
  rcLootCouncil: {
    name: 'Display character upgrades in-game with RCLootCouncil',
    href: 'https://www.curseforge.com/wow/addons/rclootcouncil',
    src: imageUrl('rclc-logo.png'),
  },
  questionablyEpic: {
    name: 'Upload QE Live Upgrade Finder reports',
    href: 'https://questionablyepic.com',
    src: imageUrl('qe-logo.png'),
  },
  email: {
    name: 'Send notifications to your team members via email',
    icon: Mail,
  },
}

const IntegrationIcon = ({
  integration: ref,
}: {
  integration: IntegrationRef
}) => {
  const integration = integrations[integrationKeyOf(ref)]
  const label = typeof ref === 'string' ? integration.name : ref.label
  const Icon = integration.icon

  const content = Icon ? (
    <Icon className='h-5 w-5 text-foreground' />
  ) : (
    <img src={integration.src} alt={label} className='h-6 w-6 object-contain' />
  )

  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>
        {integration.href ? (
          <a
            href={integration.href}
            target='_blank'
            rel='noreferrer'
            className='flex h-9 w-9 items-center justify-center rounded-md border bg-card-nested transition-colors hover:bg-accent'
          >
            {content}
          </a>
        ) : (
          <span className='flex h-9 w-9 items-center justify-center rounded-md border bg-card-nested'>
            {content}
          </span>
        )}
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )
}

export default IntegrationIcon
